import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  getSessionValue,
  removeSessionValue,
} from "../helpers/useSessionStorage";
import type {
  Cable,
  Id,
  Inspection,
  MaintenanceOperationModel,
  OperationModel,
  OperationPayload,
  StaticEquipment,
  StaticEquipmentForm,
} from "../types/models";

type QueryCallbacks<TResponse = unknown> = {
  onSuccess?: (response?: TResponse) => void;
  onError?: (error: unknown) => void;
};

type LoginPayload = {
  ship: string;
  password: string;
};

type InspectionPayload = {
  person?: string;
  notes?: string;
  date?: unknown;
};

type MaintenancePayload = {
  type?: string;
  person?: string;
  date?: unknown;
  notes?: string;
  old_location?: unknown;
  new_location?: unknown;
  old_remaining_lifespan?: number;
  new_remaining_lifespan?: number;
  old_length?: number;
  new_length?: number;
  old_diameter?: number;
  new_diameter?: number;
  old_LDBF?: number;
  new_LDBF?: number;
};

type MooringLinePayload = {
  tag?: string;
  lifespan?: number | string;
  material_type?: string;
  length?: number | string;
  diameter?: number | string;
  LDBF?: number | string;
  wear?: number | string;
  first_in_use_override?: unknown;
  initial_mooring_hours?: number | string;
  certificate_no?: string;
  certificate?: File;
};

type AddCablePayload = {
  cable_id: Id;
  arrangement?: string;
  mooring_bit_id?: Id;
};

type UpdateCablePayload = {
  cable_id: Id;
  arrangement?: string;
};

type ShipModel = {
  id: Id;
  title?: string;
  [key: string]: unknown;
};

type MooringLineModel = {
  id?: Id;
  tag?: string;
  remaining: number;
  length: number;
  diameter: number;
  LDBF: number;
  static_equipment?: { id?: Id };
  [key: string]: unknown;
};

const envBaseUrl = process.env["REACT_APP_API_BASE_URL"];

const baseUrl = (envBaseUrl || "http://localhost:3000").replace(/\/+$/, "");

export const getReportUrl = (shipId: Id) => {
  return `${baseUrl}/ships/${shipId}/report.xlsx`;
};

export const useLogin = ({ onSuccess = () => {}, onError = () => {} }: QueryCallbacks = {}) => {
  const queryClient = useQueryClient();

  const url = `${baseUrl}/users/sign_in`;

  return useMutation<unknown, unknown, LoginPayload>(
    ({ ship, password }) => {
      const options = {
        method: "POST",
        body: JSON.stringify({ name: ship, password }),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      return fetch(url, options).then(handleResponse);
    },
    {
      onSuccess: (response) => {
        onSuccess(response);

        queryClient.invalidateQueries({
          refetchActive: true,
          refetchInactive: true,
        });
      },
      onError,
    }
  );
};

export const useLogout = ({ onSuccess = () => {}, onError = () => {} }: QueryCallbacks = {}) => {
  const queryClient = useQueryClient();

  const url = `${baseUrl}/users/sign_out`;

  const token = getSessionValue("api_token");

  return useMutation(
    () => {
      const options = {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      return fetch(url, options).then(handleResponse);
    },
    {
      onSuccess: (response) => {
        onSuccess(response);

        queryClient.invalidateQueries({
          refetchActive: true,
          refetchInactive: true,
        });
      },
      onError,
    }
  );
};

export const useGetShip = (shipId: Id): ShipModel => {
  const { data: ships = [] } = useGetShips();

  const ship = ships.find((x) => String(x.id) === String(shipId));

  return ship ?? ships[0] ?? { id: shipId };
};

export const useGetShips = () => {
  return useQuery<ShipModel[]>("ships", () => {
    return fetchMe<ShipModel[]>(`${baseUrl}/ships`);
  });
};

export const useGetOperations = (shipId: Id) => {
  return useQuery<OperationModel[]>(["operations", shipId], () => {
    return fetchMe<OperationModel[]>(`${baseUrl}/ships/${shipId}/operations`);
  });
};

export const useGetOperation = (shipId: Id, operationId: Id) => {
  const response = useGetOperations(shipId);
  const operations = response.data;

  const operation = useMemo(() => {
    if (!operations) return undefined;

    return operations.find((x) => String(x.id) === String(operationId));
  }, [operations, operationId]);

  return { ...response, data: operation };
};

export const useDeleteOperation = (
  shipId: Id,
  operationId: Id | undefined,
  { onSuccess = () => {}, onError = () => {} }: QueryCallbacks = {}
) => {
  const queryClient = useQueryClient();

  return useMutation(
    () => {
      const options = {
        method: "DELETE",
      };

      return fetchMe(
        `${baseUrl}/ships/${shipId}/operations/${operationId}`,
        options
      );
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(["operations", shipId]);
        onSuccess(response);
      },
      onError,
    }
  );
};

export const useGetInspections = (shipId: Id) => {
  return useQuery<Inspection[]>(["inspections", shipId], () => {
    return fetchMe<Inspection[]>(`${baseUrl}/ships/${shipId}/inspections`);
  });
};

export const useGetInspection = (shipId: Id, inspectionId: Id) => {
  return useQuery(["inspections", shipId, inspectionId], () => {
    return fetchMe<InspectionPayload>(`${baseUrl}/ships/${shipId}/inspections/${inspectionId}`);
  });
};

export const useEditInspection = (
  shipId: Id,
  inspectionId: Id | undefined,
  { onSuccess = () => {}, onError = () => {} }: QueryCallbacks = {}
) => {
  return useMutateInspection(shipId, "PUT", inspectionId, {
    onSuccess,
    onError,
  });
};

export const useCreateInspection = (
  shipId: Id,
  { onSuccess = () => {}, onError = () => {} }: QueryCallbacks = {}
) => {
  return useMutateInspection(shipId, "POST", "", { onSuccess, onError });
};

export const useMutateInspection = (
  shipId: Id,
  method: "PUT" | "POST",
  inspectionId: Id | "" | undefined,
  {
    onSuccess = () => {},
    onError = () => {}
  }: QueryCallbacks = {}
) => {
  const queryClient = useQueryClient();

  const url = `${baseUrl}/ships/${shipId}/inspections/${inspectionId}`;

  return useMutation<unknown, unknown, InspectionPayload>(
    ({ person, notes, date }) => {
      const options = {
        method: method,
        body: JSON.stringify({ person, notes, date }),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      return fetchMe(url, options);
    },
    {
      onSuccess: (response) => {
        onSuccess(response);
        queryClient.invalidateQueries("inspections");
      },
      onError,
    }
  );
};

export const useCreateMaintenance = (
  shipId: Id,
  cableId: Id,
  { onSuccess = () => {}, onError = () => {} }: QueryCallbacks = {}
) => {
  const queryClient = useQueryClient();

  const url = `${baseUrl}/ships/${shipId}/cables/${cableId}/maintenances`;

  return useMutation<unknown, unknown, MaintenancePayload>(
    ({
      type,
      person,
      date,
      notes,
      old_location,
      new_location,
      old_remaining_lifespan,
      new_remaining_lifespan,
      old_length,
      new_length,
      old_diameter,
      new_diameter,
      old_LDBF,
      new_LDBF,
    }) => {
      const options = {
        method: "POST",
        body: JSON.stringify({
          cable_id: cableId,
          type,
          person,
          date,
          notes,
          old_location,
          new_location,
          old_remaining_lifespan,
          new_remaining_lifespan,
          old_length,
          new_length,
          old_diameter,
          new_diameter,
          old_LDBF,
          new_LDBF,
        }),
      };

      return fetchMe(url, options);
    },
    {
      onSuccess: (response) => {
        onSuccess(response);
        queryClient.invalidateQueries(["cable_maintenances", cableId]);
      },
      onError,
    }
  );
};

export const useGetMaintenances = (shipId: Id, cableId: Id) => {
  return useQuery<MaintenanceOperationModel[]>(["cable_maintenances", shipId, cableId], () => {
    return fetchMe<MaintenanceOperationModel[]>(`${baseUrl}/ships/${shipId}/cables/${cableId}/maintenances`);
  });
};

export const useGetMooringLines = (shipId: Id, historical = false) => {
  return useQuery<MooringLineModel[]>(["cables", shipId, historical], () => {
    return fetchMe<MooringLineModel[]>(
      `${baseUrl}/ships/${shipId}/cables${historical ? "?historic=true" : ""}`
    );
  });
};

export const useGetMooringLine = (shipId: Id, id: Id) => {
  return useQuery<MooringLineModel>(["cable", shipId, id], () => {
    return fetchMe<MooringLineModel>(`${baseUrl}/ships/${shipId}/cables/${id}`);
  });
};

export const useGetMooringLineOperationHistory = (shipId: Id, id: Id) => {
  return useQuery(["cable_operation_history", shipId, id], () => {
    return fetchMe(
      `${baseUrl}/ships/${shipId}/cables/${id}/historic_operations`
    );
  });
};

export const useUpdateMooringLine = (
  ship_id: Id,
  id: Id,
  { onSuccess = () => {}, onError = () => {} }: QueryCallbacks = {}
) => {
  const queryClient = useQueryClient();

  const url = `${baseUrl}/ships/${ship_id}/cables/${id}/`;

  return useMutation<unknown, unknown, MooringLinePayload>(
    ({
      tag,
      lifespan,
      material_type,
      length,
      diameter,
      LDBF,
      wear,
      first_in_use_override,
      initial_mooring_hours,
      certificate_no,
      certificate,
    }) => {
      const formData = new FormData();

      appendFormValue(formData, "cable[tag]", tag);
      appendFormValue(formData, "cable[lifespan]", lifespan);
      appendFormValue(formData, "cable[material_type]", material_type);
      appendFormValue(formData, "cable[length]", length);
      appendFormValue(formData, "cable[diameter]", diameter);
      appendFormValue(formData, "cable[LDBF]", LDBF);
      appendFormValue(formData, "cable[wear]", wear);
      appendFormValue(formData, "cable[first_in_use_override]", first_in_use_override);
      appendFormValue(formData, "cable[initial_mooring_hours]", initial_mooring_hours);
      if (certificate) {
        formData.append("cable[certificate]", certificate);
      }
      appendFormValue(formData, "cable[certificate_no]", certificate_no);

      const options = {
        method: "PUT",
        body: formData,
        headers: {
          Accept: "application/json",
        },
      };

      return fetchMe(url, options);
    },
    {
      onSuccess: (response) => {
        onSuccess(response);
        queryClient.invalidateQueries(["cable", id]);
        queryClient.invalidateQueries("cables");
      },
      onError,
    }
  );
};

export const useCreateMooringLine = (ship_id: Id, {
  onSuccess = () => {},
  onError = () => {}
}: QueryCallbacks = {}) => {
  const queryClient = useQueryClient();

  const url = `${baseUrl}/ships/${ship_id}/cables/`;

  return useMutation<unknown, unknown, MooringLinePayload>(
    ({
      tag,
      lifespan,
      material_type,
      length,
      diameter,
      LDBF,
      wear,
      first_in_use_override,
      initial_mooring_hours,
      certificate_no,
      certificate,
    }) => {
      const formData = new FormData();

      if (tag) {
        formData.append("cable[tag]", tag);
      }
      if (lifespan) {
        appendFormValue(formData, "cable[lifespan]", lifespan);
      }
      if (material_type) {
        formData.append("cable[material_type]", material_type);
      }
      if (length) {
        appendFormValue(formData, "cable[length]", length);
      }
      if (diameter) {
        appendFormValue(formData, "cable[diameter]", diameter);
      }
      if (LDBF) {
        appendFormValue(formData, "cable[LDBF]", LDBF);
      }
      if (wear) {
        appendFormValue(formData, "cable[wear]", wear);
      }
      appendFormValue(formData, "cable[first_in_use_override]", first_in_use_override);
      appendFormValue(formData, "cable[initial_mooring_hours]", initial_mooring_hours);
      if (certificate) {
        formData.append("cable[certificate]", certificate);
      }
      if (certificate_no) {
        formData.append("cable[certificate_no]", certificate_no);
      }

      const options = {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
        },
      };

      return fetchMe(url, options);
    },
    {
      onSuccess: (response) => {
        onSuccess(response);
        queryClient.invalidateQueries("cables");
      },
      onError,
    }
  );
};

export const useGetEquipment = (shipId: Id) => {
  return useQuery<StaticEquipment[]>(
    ["equipment", shipId],
    () => {
      return fetchMe<StaticEquipment[]>(`${baseUrl}/ships/${shipId}/static_equipments`);
    },
    {
      enabled: shipId !== undefined,
    }
  );
};

// Cables that can be passed from a mooring bit: those assigned to a spare
// location OR unassigned (i.e. not on a winch). Unassigned cables have no
// static equipment, so they never appear in /static_equipments — this
// endpoint is the correct source for the mooring-bit line picker.
export const useGetSpareCables = (shipId: Id) => {
  return useQuery<Cable[]>(
    ["spareCables", shipId],
    () => {
      return fetchMe<Cable[]>(`${baseUrl}/ships/${shipId}/cables/spares`);
    },
    {
      enabled: shipId !== undefined,
    }
  );
};

export const useGetStaticEquipment = (shipId: Id, staticEquipmentId: Id) => {
  return useQuery<StaticEquipment>(
    ["staticEquipment", shipId, staticEquipmentId],
    () => {
      return fetchMe<StaticEquipment>(
        `${baseUrl}/ships/${shipId}/static_equipments/${staticEquipmentId}`
      );
    },
    {
      enabled: shipId !== undefined && staticEquipmentId !== undefined,
    }
  );
};

export const useCreateStaticEquipment = (
  shipId: Id,
  { onSuccess = () => {}, onError = () => {} }: QueryCallbacks = {}
) => {
  const queryClient = useQueryClient();
  const url = `${baseUrl}/ships/${shipId}/static_equipments/`;

  return useMutation<unknown, unknown, StaticEquipmentForm>(
    ({
      type,
      direction,
      station_name,
      position_on_deck_x,
      position_on_deck_y,
      location,
    }) => {
      const options = {
        method: "POST",
        body: JSON.stringify({
          static_equipment: {
            type,
            direction,
            station_name,
            position_on_deck_x,
            position_on_deck_y,
            location,
          },
        }),
      };

      return fetchMe(url, options);
    },
    {
      onSuccess: (response) => {
        onSuccess(response);
        queryClient.invalidateQueries(["equipment", shipId]);
      },
      onError,
    }
  );
};

export const useUpdateStaticEquipment = (
  shipId: Id,
  staticEquipmentId: Id,
  { onSuccess = () => {}, onError = () => {} }: QueryCallbacks = {}
) => {
  const queryClient = useQueryClient();
  const url = `${baseUrl}/ships/${shipId}/static_equipments/${staticEquipmentId}`;

  return useMutation<unknown, unknown, StaticEquipmentForm>(
    ({
      type,
      direction,
      station_name,
      position_on_deck_x,
      position_on_deck_y,
      location,
    }) => {
      const options = {
        method: "PUT",
        body: JSON.stringify({
          static_equipment: {
            type,
            direction,
            station_name,
            position_on_deck_x,
            position_on_deck_y,
            location,
          },
        }),
      };

      return fetchMe(url, options);
    },
    {
      onSuccess: (response) => {
        onSuccess(response);
        queryClient.invalidateQueries(["equipment", shipId]);
        queryClient.invalidateQueries([
          "staticEquipment",
          shipId,
          staticEquipmentId,
        ]);
      },
      onError,
    }
  );
};

export const useGetOperationEquipment = (
  shipId: Id,
  operationId: Id,
  operationEquipmentId: Id
) => {
  return useQuery(
    ["operationEquipment", shipId, operationId, operationEquipmentId],
    () => {
      return fetchMe(
        `${baseUrl}/ships/${shipId}/operations/${operationId}/operation_equipments/${operationEquipmentId}`
      );
    }
  );
};

export const useCreateOperation = (
  shipId: Id,
  { onSuccess = () => {}, onError = () => {} }: QueryCallbacks = {}
) => {
  const queryClient = useQueryClient();

  const url = `${baseUrl}/ships/${shipId}/operations`;

  return useMutation<unknown, unknown, OperationPayload>(
    ({ country, port, terminal, firstLine, moored, completed }) => {
      const options = {
        method: "POST",
        body: JSON.stringify({
          country,
          port,
          terminal,
          first_line: firstLine,
          moored,
          completed,
          type: "mooring",
        }),
      };

      return fetchMe(url, options);
    },
    {
      onSuccess: (response) => {
        onSuccess(response);
        queryClient.invalidateQueries("operations");
      },
      onError,
    }
  );
};

export const useUpdateOperation = (
  shipId: Id,
  operationId: Id | undefined,
  { onSuccess = () => {}, onError = () => {} }: QueryCallbacks = {}
) => {
  const queryClient = useQueryClient();

  const url = `${baseUrl}/ships/${shipId}/operations/${operationId}`;

  return useMutation<unknown, unknown, OperationPayload>(
    ({ country, port, terminal, firstLine, moored, completed }) => {
      const options = {
        method: "PUT",
        body: JSON.stringify({
          country,
          port,
          terminal,
          first_line: firstLine,
          moored,
          completed,
        }),
      };

      return fetchMe(url, options);
    },
    {
      onSuccess: (response) => {
        if (onSuccess) {
          onSuccess(response);
        }
        queryClient.invalidateQueries("operations");
      },
      onError,
    }
  );
};

export const useUpdateOperationSide = (
  shipId: Id,
  operationId: Id | undefined,
  { onSuccess = () => {}, onError = () => {} }: QueryCallbacks = {}
) => {
  const queryClient = useQueryClient();
  const url = `${baseUrl}/ships/${shipId}/operations/${operationId}`;
  return useMutation<unknown, unknown, { side: string }>(
    ({ side }) => {
      const options = {
        method: "PUT",
        body: JSON.stringify({ side }),
      };
      return fetchMe(url, options);
    },
    {
      onSuccess: (response) => {
        if (onSuccess) {
          onSuccess(response);
        }
        // TODO: Be effective and replace the one operation
        queryClient.invalidateQueries("operations");
      },
      onError,
    }
  );
};

export const useAddCable = (
  shipId: Id,
  operationId: Id,
  { onSuccess = () => {} }: QueryCallbacks = {}
) => {
  const queryClient = useQueryClient();

  const url = `${baseUrl}/ships/${shipId}/operations/${operationId}/operation_equipments/`;

  return useMutation<unknown, unknown, AddCablePayload>(
    ({ cable_id, arrangement, mooring_bit_id }) => {
      const options = {
        method: "POST",
        body: JSON.stringify({ cable_id, arrangement, mooring_bit_id }),
      };

      return  fetchMe(url, options);
    },
    {
      onSuccess: (response) => {
        onSuccess(response);
        queryClient.invalidateQueries("operations");
      },
    }
  );
};

export const useUpdateCable = (
  shipId: Id,
  operationId: Id,
  operationEquipmentId: Id,
  {
    onSuccess
  }: QueryCallbacks = {}
) => {
  const queryClient = useQueryClient();

  const url = `${baseUrl}/ships/${shipId}/operations/${operationId}/operation_equipments/${operationEquipmentId}`;

  return useMutation<unknown, unknown, UpdateCablePayload>(
    ({ cable_id, arrangement }) => {
      const options = {
        method: "PUT",
        body: JSON.stringify({ cable_id, arrangement }),
      };

      return fetchMe(url, options);
    },
    {
      onSuccess: (response) => {
        if (onSuccess) {
          onSuccess(response);
        }
        queryClient.invalidateQueries("operations");
      },
    }
  );
};

export const useRemoveCable = (shipId: Id, operationId: Id | undefined, {
  onSuccess
}: QueryCallbacks = {}) => {
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, Id>(
    (operationEquipmentId) => {
      const url = `${baseUrl}/ships/${shipId}/operations/${operationId}/operation_equipments/${operationEquipmentId}`;

      const options = {
        method: "DELETE",
      };

      return fetchMe(url, options);
    },
    {
      onSuccess: (response) => {
        if (onSuccess) {
          onSuccess(response);
        }
        queryClient.invalidateQueries("operations");
      },
    }
  );
};

// Private
const appendFormValue = (formData: FormData, key: string, value: unknown) => {
  if (value !== undefined && value !== null) {
    formData.append(key, String(value));
  }
};

const fetchMe = <T = unknown>(url: string, options: RequestInit = {}): Promise<T> => {
  const token = getSessionValue("api_token");

  const headers = new Headers(options.headers ?? {});
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  if (!headers.has("Content-Type") && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  const optionsWithToken: RequestInit = {
    ...options,
    headers,
  };
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  return fetch(url, optionsWithToken).then(handleResponse) as Promise<T>;
};

const handleResponse = async (response: Response): Promise<unknown> => {
  if (response.status === 401) {
    if (getSessionValue("api_token")) {
      removeSessionValue("api_token");
    }
  }

  if (response.status === 204) {
    return;
  }

  if (response.status < 400) {
    return response.json();
  }

  const message = await response.json();

  const error = new Error("Request failed");
  Object.assign(error, {
    code: response.status,
    message,
  });
  throw error;
};
